import React, { useState, useCallback, useEffect } from 'react';
import {
  Page,
  Card,
  DataTable,
  TextField,
  Button,
  Stack,
  Text,
  Modal,
  FormLayout,
  Banner,
  Badge,
  ButtonGroup,
  Tooltip,
  Icon,
  Box,
} from '@shopify/polaris';
import { EditMinor, DeleteMinor, StoreMajor } from '@shopify/polaris-icons';
import { useShop } from '../contexts/ShopContext';
import { useNavigate } from 'react-router-dom';

function ShopManager() {
  const [showModal, setShowModal] = useState(false);
  const [editingShop, setEditingShop] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { shops, updateShop, deleteShop, setCurrentShop, currentShop } = useShop();
  const navigate = useNavigate();

  const handleSaveShop = useCallback(async () => {
    try {
      if (!editingShop?.name?.trim()) {
        setError('Shop name is required');
        return;
      }
      if (!editingShop?.accessToken?.trim()) {
        setError('Access token is required');
        return;
      }

      const shopData = {
        name: editingShop.name.trim(),
        accessToken: editingShop.accessToken.trim(),
      };

      await updateShop(editingShop.id, shopData);
      setSuccess('Shop updated successfully');
      setShowModal(false);
      setEditingShop(null);
    } catch (err) {
      setError(err.message || 'Failed to update shop');
    }
  }, [editingShop, updateShop]);

  const handleDeleteShop = useCallback(async (id) => {
    try {
      await deleteShop(id);
      setSuccess('Shop deleted successfully');
      // If we deleted the current shop, clear it
      if (currentShop?.id === id) {
        setCurrentShop(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete shop');
    }
  }, [deleteShop, currentShop, setCurrentShop]);

  const handleSetCurrentShop = useCallback(async (shop) => {
    try {
      await setCurrentShop(shop); // Wait for the context to update Firebase
      setSuccess(`${shop.name} is now your active shop`);
    } catch (err) {
      console.error('Error setting active shop:', err);
      setError(err.message || 'Failed to set active shop');
    }
  }, [setCurrentShop]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const rows = shops?.map((shop) => [
    <Stack vertical spacing="tight">
      <Text variant="bodyMd" fontWeight="bold">
        {shop.name}
      </Text>
      <Text variant="bodySm" color="subdued">
        {shop.shop}
      </Text>
    </Stack>,
    <Badge status={currentShop?.id === shop.id ? 'success' : 'default'}>
      {currentShop?.id === shop.id ? 'Active' : 'Inactive'}
    </Badge>,
    <Text variant="bodySm">
      {new Date(shop.createdAt).toLocaleDateString()}
    </Text>,
    <ButtonGroup>
      <Button
        size="slim"
        onClick={() => handleSetCurrentShop(shop)}
        disabled={currentShop?.id === shop.id}
      >
        Set Active
      </Button>
      <Button
        size="slim"
        onClick={() => navigate(`/app/shops/${shop.id}`)}
      >
        View Details
      </Button>
    </ButtonGroup>,
    <ButtonGroup>
      <Tooltip content="Edit Shop">
        <Button
          size="slim"
          onClick={() => {
            setEditingShop(shop);
            setShowModal(true);
          }}
          icon={<Icon source={EditMinor} />}
        />
      </Tooltip>
      <Tooltip content="Delete Shop">
        <Button
          size="slim"
          destructive
          onClick={() => handleDeleteShop(shop.id)}
          icon={<Icon source={DeleteMinor} />}
        />
      </Tooltip>
    </ButtonGroup>
  ]) || [];

  return (
    <Page
      title="Shop Manager"
      primaryAction={{
        content: 'Connect New Shop',
        onAction: () => navigate('/app/connect'),
      }}
    >
      {error && (
        <Box paddingBlockEnd="4">
          <Banner status="critical">{error}</Banner>
        </Box>
      )}
      {success && (
        <Box paddingBlockEnd="4">
          <Banner status="success">{success}</Banner>
        </Box>
      )}

      <Card>
        {rows.length > 0 ? (
          <DataTable
            columnContentTypes={['text', 'text', 'text', 'text', 'text']}
            headings={[
              'Shop Name',
              'Status',
              'Created Date',
              'Actions',
              'Management'
            ]}
            rows={rows}
            defaultSortDirection="descending"
          />
        ) : (
          <Box padding="4">
            <Banner status="info">
              <Stack vertical spacing="tight">
                <Stack spacing="tight" alignment="center">
                  <Icon source={StoreMajor} />
                  <Text variant="headingMd">No shops connected</Text>
                </Stack>
                <Text>Connect your first Shopify store to get started with AI-powered product content generation.</Text>
                <Box paddingBlockStart="3">
                  <Button primary onClick={() => navigate('/app/connect')}>
                    Connect New Shop
                  </Button>
                </Box>
              </Stack>
            </Banner>
          </Box>
        )}
      </Card>

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingShop(null);
        }}
        title="Edit Shop"
        primaryAction={{
          content: 'Save',
          onAction: handleSaveShop,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowModal(false);
              setEditingShop(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Shop Name"
              value={editingShop?.name || ''}
              onChange={(value) =>
                setEditingShop((prev) => ({ ...prev, name: value }))
              }
              error={!editingShop?.name?.trim() ? 'Name is required' : ''}
              autoComplete="off"
            />
            <TextField
              label="Access Token"
              value={editingShop?.accessToken || ''}
              type="password"
              onChange={(value) =>
                setEditingShop((prev) => ({ ...prev, accessToken: value }))
              }
              error={!editingShop?.accessToken?.trim() ? 'Access token is required' : ''}
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
    </Page>
  );
}

export default ShopManager;
